import React, { useState } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

/**
 * Type definition for the RenderTextField component props.
 * @property {string} name - The name attribute for the input field
 * @property {string} label - The label text to display
 * @property {string} value - The current value of the input
 * @property {boolean} error - Whether the field has an error
 * @property {string} helperText - Helper text or error message to display
 * @property {any} handleBlur - Blur event handler
 * @property {any} handleChange - Change event handler
 * @property {string} [type="text"] - Input type (e.g., "text", "password")
 * @property {string} [span="2"] - Grid column span for layout
 * @property {boolean} [disabled=false] - Whether the field is disabled
 * @property {string} [mt="0px"] - Margin top value
 * @property {boolean} [multiline=false] - Whether to allow multiple lines
 * @property {number} [rows=1] - Number of rows for multiline input
 * @property {boolean} [required=false] - Whether the field is required
 */
type RenderTextFieldProps = {
  name: string;
  label: string;
  value: string;
  error: boolean;
  helperText: string;
  handleBlur: any;
  handleChange: any;
  type?: string;
  span?: string;
  disabled?: boolean;
  mt?: string;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
};

/**
 * A reusable text field component with enhanced features and styling.
 * 
 * This component provides a styled text input with support for:
 * - Password visibility toggle
 * - Error states and messages
 * - Multiline input
 * - Grid layout integration
 * - Custom styling and margins
 * 
 * @component
 * @param {RenderTextFieldProps} props - The props passed to the component
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {string} props.value - Field value
 * @param {boolean} props.error - Error state
 * @param {string} props.helperText - Helper/error text
 * @param {any} props.handleBlur - Blur handler
 * @param {any} props.handleChange - Change handler
 * @param {string} [props.type="text"] - Input type
 * @param {string} [props.span="2"] - Grid span
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {string} [props.mt="0px"] - Top margin
 * @param {boolean} [props.multiline=false] - Multiline mode
 * @param {number} [props.rows=1] - Number of rows
 * @param {boolean} [props.required=false] - Required field
 * 
 * @example
 * ```tsx
 * <RenderTextField
 *   name="password"
 *   label="Password"
 *   value={password}
 *   error={!!errors.password}
 *   helperText={errors.password}
 *   handleBlur={handleBlur}
 *   handleChange={handleChange}
 *   type="password"
 *   required
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI TextField component
 * - Supports password visibility toggle
 * - Includes consistent font sizing
 * - Handles grid layout positioning
 * - Supports form validation states
 * - Provides multiline input capability
 * - Maintains consistent styling with the application theme
 * 
 * @returns {JSX.Element} A React component that renders a text field
 */
const RenderTextField: React.FC<RenderTextFieldProps> = ({
  name,
  label,
  value,
  error,
  helperText,
  handleBlur,
  handleChange,
  type = "text",
  span = "2",
  disabled = false,
  mt = "0px",
  multiline = false,
  rows = 1,
  required = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const isPasswordField = type === "password";

  return (
    <TextField
      required={required}
      fullWidth
      variant="filled"
      type={isPasswordField && showPassword ? "text" : type}
      label={label}
      onBlur={handleBlur}
      onChange={handleChange}
      value={value}
      name={name}
      error={error}
      helperText={helperText}
      FormHelperTextProps={{
        style: {
          fontSize: "1rem",
        },
      }}
      inputProps={{
        style: {
          fontSize: "1.2rem",
        },
      }}
      sx={{
        gridColumn: `span ${span}`,
        marginTop: `${mt}`,
        paddingRight: `15px`,
      }}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      InputProps={{
        endAdornment: isPasswordField && (
          <InputAdornment position="end">
            <IconButton
              onClick={handleClickShowPassword}
              edge="end"
              aria-label="toggle password visibility"
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default RenderTextField;
